'use client';

import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import { DISCONTINUED_MESSAGE, PRODUCT_TYPE, SALE_STATUS } from '@/constants';
import { SIZE_SELECT_FROM } from '@/features/gfUserDesign/constants';
import { useGlobalLoadingAtom } from '@/state';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { markShareOrigin } from '@/utils/shareOrigin';
import type { Route } from 'next';
import { notFound, useParams, useRouter } from 'next/navigation';
import { useState } from 'react';
import type { SharedDesignData } from '../../types';
import DesignCtaButton from '../DesignCtaButton/DesignCtaButton';
import DesignPreviewImage from '../DesignPreviewImage/DesignPreviewImage';
import DesignProductSection from '../DesignProductSection/DesignProductSection';
import DesignWorkSection from '../DesignWorkSection/DesignWorkSection';
import styles from './GfDesignShareContainer.module.scss';

function GfDesignShareContainer({ designData, shareToken }: { designData: SharedDesignData; shareToken?: string }) {
  const router = useRouter();
  const params = useParams();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const firstItem = designData.items[0];

  if (!firstItem || !firstItem.prints || firstItem.prints.length === 0) {
    notFound();
  }

  // 終売判定
  const isDiscontinued = firstItem.productSaleStatus === SALE_STATUS.discontinued;

  // クライアントでも確実に取得するため URL の shareToken を参照（props が undefined になるケース対策）
  const effectiveShareToken = shareToken ?? (typeof params?.shareToken === 'string' ? params.shareToken : '');

  // シェア対象の印刷エリアを sharedPrintAreaId で特定（一致しない場合は先頭にフォールバック）
  const sharedPrint =
    firstItem.prints.find((p) => p.printAreaId === firstItem.sharedPrintAreaId) ?? firstItem.prints[0];

  const creatorNameList: string[] =
    firstItem.workAuthors && firstItem.workAuthors.length > 0 ? firstItem.workAuthors.map((author) => author.name) : [];

  const isTemplateProduct = firstItem.productType === PRODUCT_TYPE.template;

  const attributes = isTemplateProduct
    ? [{ label: 'カラー', value: firstItem.backgroundVariantName ?? '' }]
    : [
        { label: 'カラー', value: firstItem.productColor },
        { label: 'サイズ', value: firstItem.productSize },
      ];

  // テンプレ商品はオプション選択画面（/product/{id}）へ、非テンプレはサイズ選択画面へ遷移する
  const handleGoDesignOptionSelect = () => {
    if (isTemplateProduct && firstItem.productId && firstItem.materialId) {
      // ボタン押下＝シェアデザインから作成開始。この後の add_to_cart まで流入フラグを保持する
      markShareOrigin();
      startGlobalLoading();
      const query = new URLSearchParams({
        materialId: String(firstItem.materialId),
        workId: String(firstItem.workId),
      }).toString();
      router.push(`/product/${firstItem.productId}?${query}` as Route);
      return;
    }

    if (!firstItem?.userPrintId || !effectiveShareToken) return;
    // ボタン押下＝シェアデザインから作成開始。この後の add_to_cart まで流入フラグを保持する
    markShareOrigin();
    startGlobalLoading();
    const userPrintId =
      typeof firstItem.userPrintId === 'string' ? firstItem.userPrintId : String(firstItem.userPrintId);
    const callbackUrl = `/design/${effectiveShareToken}`;
    const query = new URLSearchParams({ from: SIZE_SELECT_FROM.share, callbackUrl }).toString();
    router.push(`/user-designs/${userPrintId}/size-select?${query}`);
  };

  return (
    <div className={layoutStyles.page}>
      <div className={`${layoutStyles.gfPageContainer} ${utilsStyles.mtMediumNoMediaDiff}`}>
        <div className={styles.shareInner}>
          <div className={styles.designCard}>
            <div className={styles.contentRow}>
              {/* 左側: プレビュー画像 */}
              <div className={styles.leftColumn}>
                <DesignPreviewImage
                  userPrints={firstItem.prints}
                  workName={firstItem.workName}
                  onImageClick={(url) => {
                    setSelectedImageUrl(url);
                    setIsImageModalOpen(true);
                  }}
                />
                {/* SP版のアクションボタンを表示 */}
                <div className={`${styles.spActionButtonContainer} ${utilsStyles.onlySp}`}>
                  {isDiscontinued && <p className={styles.discontinuedMessage}>{DISCONTINUED_MESSAGE}</p>}
                  <DesignCtaButton
                    onClick={handleGoDesignOptionSelect}
                    text='このデザインからグッズをつくる'
                    size='fullWidthLarge'
                    disabled={isDiscontinued}
                  />
                </div>
              </div>

              {/* 右側: 作品情報 */}
              <div className={styles.rightColumn}>
                <DesignWorkSection
                  thumbnailUrl={firstItem.workLogoUrl ?? sharedPrint.previewImageUrl}
                  workName={firstItem.workName}
                  creatorNameList={creatorNameList}
                  workId={firstItem.workId}
                />
                <DesignProductSection productName={firstItem.productName} attributes={attributes} />
              </div>
            </div>
          </div>
          {/* 作品詳細ページへ遷移 */}
          {/* PC版のアクションボタンを表示 */}
          <div className={`${styles.buttonContainer} ${utilsStyles.onlyPc}`}>
            <div className={styles.ctaButtonSection}>
              {isDiscontinued && <p className={styles.discontinuedMessage}>{DISCONTINUED_MESSAGE}</p>}
              <DesignCtaButton
                onClick={handleGoDesignOptionSelect}
                text='このデザインからグッズをつくる'
                size='fullWidthLarge'
                disabled={isDiscontinued}
              />
            </div>
          </div>
        </div>
      </div>
      <ImageDetailModal
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setSelectedImageUrl('');
        }}
        imageUrl={selectedImageUrl || null}
        alt='デザイン拡大プレビュー'
      />
    </div>
  );
}

export default GfDesignShareContainer;
